import * as React from "react"
import { Head, useForm } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Button } from "@/components/ui/button"
import { Combobox } from "@/components/ui/combobox"

const DESCRIPTION =
  "Choose the time zone the app shows every automation schedule in."

type TimezoneOption = { value: string; label: string; offset: string }

export default function Settings({
  timezone,
  timezone_options,
}: {
  timezone: string | null
  timezone_options: TimezoneOption[]
}) {
  const { data, setData, transform, patch, processing, isDirty } = useForm({
    timezone: timezone ?? "",
  })

  const options = React.useMemo(
    () =>
      timezone_options.map((option) => ({
        value: option.value,
        label: `${option.label} (${option.offset})`,
      })),
    [timezone_options],
  )

  const submit = (event: React.FormEvent) => {
    event.preventDefault()

    // Nested explicitly: SettingsController has no matching model, so Rails'
    // ParamsWrapper never produces the `user` key the controller reads, and a
    // flat post saved a blank time zone instead of the chosen one.
    transform((form) => ({ user: form }))
    patch("/settings")
  }

  return (
    <>
      <Head title="Settings">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Settings" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <PageHeader
          title="Settings"
          description="Your own preferences. Automation setups are shared with your organization and live under Automations."
        />

        <div className="mt-6">
          <FlashNotice />
        </div>

        <form onSubmit={submit} className="mt-2 max-w-lg space-y-4">
          <div className="space-y-2">
            <label htmlFor="timezone">Time zone</label>
            <Combobox
              options={options}
              value={data.timezone || null}
              onChange={(value) => setData("timezone", value)}
              placeholder="Select a time zone…"
              searchPlaceholder="Type to filter…"
              triggerLabel="Time zone"
            />
            <p className="text-xs text-ink-muted">
              Schedules are shown to you in this zone. It was detected from your
              browser the first time you signed in; change it here if that
              isn&rsquo;t where you work.
            </p>
          </div>

          <Button type="submit" disabled={processing || !isDirty}>
            {processing ? "Saving…" : "Save"}
          </Button>
        </form>
      </AppShell>
    </>
  )
}
